import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode, type Ref } from 'react';
import { ApiError, changePassword, login } from './api/client';
import {
  previousCompanyId,
  returnToCompany,
  setCompany,
  setMustChangePassword,
  setSession,
  snapshot,
  subscribe,
} from './auth/session';
import { RouterProvider } from '@tanstack/react-router';
import { Button } from './components/Button';
import { Field } from './components/Field';
import { Input } from './components/Input';
import { PasswordInput } from './components/PasswordInput';
import { Banner } from './components/Banner';
import { EmptyState } from './components/EmptyState';
import { adviseOnRefusal, ErrorState } from './components/ErrorState';
import { Skeleton } from './components/Skeleton';
import { Stack } from './components/layout/Stack';
import { Cluster } from './components/layout/Cluster';
// DS-11b2: the signed-out frame, shared with the portal and the console by its own file (plan C1).
import { SignInFrame } from './components/SignInFrame';
import { t, useLang } from './modules/i18n';
import { useMemberships } from './modules/company-format-provider';
import { LangToggle, prefetchDashboard, router } from './routes';
import { startTill, isTillPath, Till } from './till-entry';

// DS-11a0: the till is its own chunk (owner decision, 2026-10-07); on a till path it is asked for now. See till-entry.tsx.
startTill();
// DS-11a4: a refusal for want of a permission says to ask an administrator of this company — the back office's
// advice only; the portal and the console keep the server's sentence (ErrorState.tsx, plan C9.4).
adviseOnRefusal(true);

/**
 * The shell: sign in, choose a company, then the product.
 *
 * A COMPANY IS NOT OPTIONAL. Every route that is not public or installation-level refuses without
 * `X-Company-Id`, so this is not a preference screen that can be skipped — it is the second half of
 * signing in, and the app has nothing to show until it is answered.
 */
export function App() {
  /**
   * 🔴 THE TILL IS A DIFFERENT APPLICATION THAT HAPPENS TO SHIP IN THIS BUILD — ERP Phase 4
   * increment 11b. Since DS-11a0 it is a chunk of its own, fetched only on a till path (`till-entry.tsx`).
   *
   * §4 asks for it *"at `/pos/` on the same origin, device realm"*, and the same origin is the point:
   * a till on a shop's network reaches one host. What it must NOT share is this shell — a tablet on
   * a counter has no sign-in form, no company picker and no sidebar, because it belongs to one shop
   * and is operated by whoever is standing at it.
   *
   * ⚠️ BEFORE THE SESSION CHECK, deliberately. A till has no staff session until a PIN makes one,
   * so anything that asked for a password first would be asking the wrong person for the wrong
   * credential.
   */
  /**
   * ⚠️ READ BEFORE THE HOOKS AND RETURNED AFTER THEM. An early return above `useSyncExternalStore`
   * would call a different number of hooks on `/pos` than everywhere else — which happens to work
   * here, because the path cannot change without a reload, and is exactly the reasoning that makes a
   * rules-of-hooks violation survive until the day somebody adds client-side navigation.
   */
  // DS-2a review: not `/post-dated-cheques`, and not the back office's /pos/registers and /pos/sessions (isTillPath).
  const isTill = isTillPath(window.location.pathname);
  const session = useSyncExternalStore(subscribe, snapshot, () => null);
  if (isTill) return <Till />;
  if (!session) return <SignIn />;
  // BEFORE the company picker. The API refuses every route but this one while the flag is set, so
  // an app that asked for a company first would show a picker whose own request 403s.
  if (session.mustChangePassword) return <ChangePassword />;
  if (!session.companyId) return <ChooseCompany />;
  return <RouterProvider router={router} />;
}

/**
 * The forced password change.
 *
 * Not an edge case: `db:seed` sets this on the first admin of every install, and a staff reset sets
 * it again — so this is the FIRST screen a new customer sees after signing in. Without it a fresh
 * install is unusable, which it was until now.
 *
 * The server issues a new token pair here and revokes every other session for the account, because
 * the old password may be known to whoever set it.
 */
function ChangePassword() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && newPassword !== confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      const result = await changePassword(currentPassword, newPassword);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        companyId: null,
        mustChangePassword: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centred title="Choose a new password">
      <p className="app-lede">
        This account was created with a temporary password, so it cannot be used until you replace
        it. Every other session signed in as you will be signed out.
      </p>
      <form className="app-form" onSubmit={submit}>
        <Field label="Current password">
          <PasswordInput
            value={currentPassword}
            autoComplete="current-password"
            autoFocus
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </Field>
        <Field label="New password" hint="At least 12 characters.">
          <PasswordInput
            value={newPassword}
            autoComplete="new-password"
            minLength={12}
            onChange={(e) => setNew(e.target.value)}
            required
          />
        </Field>
        <Field label="Confirm new password" error={mismatch ? 'The two do not match.' : undefined}>
          <PasswordInput
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </Field>
        {error ? <Banner tone="danger" title="Could not change your password" announce>{error}</Banner> : null}
        <Button variant="primary" type="submit" busy={busy} disabled={mismatch}>
          Set password and continue
        </Button>
      </form>
    </Centred>
  );
}

/**
 * The back office's signed-out frame — `SignInFrame` (DS-11b2, §1.7; F372): the Wordmark, the language toggle beside it
 * (the way into Bangla before signing in; the forms themselves stay English, like every screen — assumption A5), and
 * the help line in the chrome's language (WCAG 3.2.6, 3.1.2). The sign-in, the forced password change, "Choose a
 * company" and "No companies yet" are all drawn in it, and each names the tab (DS-11b1; `tab`, `null`: see the frame).
 */
function Centred(props: { title: string; tab?: string | null; titleRef?: Ref<HTMLHeadingElement>; children: ReactNode }) {
  const lang = useLang();
  return (
    <SignInFrame
      {...props}
      word="Workbook"
      tools={<LangToggle lang={lang} />}
      help={`${t(lang, 'Need help?')} ${t(lang, 'Ask an administrator of your company.')}`}
      helpLang={lang}
    />
  );
}

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email, password, totpCode || undefined);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        // Chosen next. Deliberately not guessed from the first membership — see ChooseCompany.
        companyId: null,
        // The server says so on the way in; the 403 path below is the backstop for a session that
        // predates a staff reset.
        mustChangePassword: result.user.mustChangePassword,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Sign in failed.';
      // The server asks for a code by refusing without one; it does not pre-announce that an
      // account has two-factor, which would tell an attacker which accounts are worth the effort.
      if (/totp|two-factor|code/i.test(message)) setNeedsTotp(true);
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centred title="Sign in to Workbook" tab="Sign in">
      <form className="app-form" onSubmit={submit}>
        {/* DS-11b2 (F372): the page says what it is for, as the portal's and the console's always did. */}
        <p className="app-lede">Use the email address and password your company's administrator gave you.</p>
        <Field label="Email">
          {/* DS-11b2 (F377): the only form on the page has the caret, so nobody has to Tab or click into it. */}
          <Input
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password">
          <PasswordInput
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        {needsTotp ? (
          <Field label="Authentication code" hint="The six-digit code from your authenticator app.">
            {/* DS-11b2 (F377): it appears when the server asks for it, and takes the caret, the next thing to type. */}
            <Input
              inputMode="numeric"
              value={totpCode}
              autoComplete="one-time-code"
              autoFocus
              onChange={(e) => setTotp(e.target.value)}
            />
          </Field>
        ) : null}
        {error ? <Banner tone="danger" title="Could not sign in" announce>{error}</Banner> : null}
        <Button variant="primary" type="submit" busy={busy}>
          Sign in
        </Button>
      </form>
    </Centred>
  );
}

function ChooseCompany() {
  /**
   * 🔴 DS-11b2 (F63): THE SHELL'S OWN QUERY, NOT A FETCH OF ITS OWN. The picker read the list by hand and the shell
   * asked again on its own key, so every sign-in fetched it twice and the bar's company button waited for the second.
   * Now both read `useMemberships()`, and the shell opens on the answer this page already has. Try again is the query's
   * refetch; a refusal because the password must change first sends the person there, as it did.
   */
  const memberships = useMemberships();
  const failure = memberships.error;
  const mustChange = failure instanceof ApiError && failure.needsPasswordChange;
  /**
   * DS-11a4 review (F53, WCAG 2.4.3): the bar's company button, or the sign-in form, unmounted with the screen it was
   * on, so focus fell to <body>. It starts at this screen's heading instead (one element across loading, list and
   * failure: the same Centred in the same place). Only when focus was lost — never taken from somewhere it was put.
   */
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!document.activeElement || document.activeElement === document.body) heading.current?.focus();
    // DS-11b2 (F483): the page a company opens on, fetched while the person chooses — not after the shell has drawn.
    void prefetchDashboard();
  }, []);
  useEffect(() => {
    if (mustChange) setMustChangePassword(true);
  }, [mustChange]);

  if (memberships.isError && !memberships.isFetching && !mustChange) {
    return (
      <Centred title="Choose a company" titleRef={heading}>
        <Stack gap={8}>
          <ErrorState title="Could not load your companies" error={failure} onRetry={() => void memberships.refetch()} />
          <Cluster>
            <Button onClick={() => setSession(null)}>Sign out</Button>
          </Cluster>
        </Stack>
      </Centred>
    );
  }
  const list = memberships.data;
  if (!list || memberships.isError) {
    return (
      <Centred title="Choose a company" titleRef={heading}>
        <Skeleton label="your companies" />
      </Centred>
    );
  }

  if (list.length === 0) {
    return (
      <Centred title="No companies yet" titleRef={heading}>
        <EmptyState
          title="Your account is not a member of any company."
          hint="Somebody with `members:manage` has to add you to one before there is anything to see."
        />
      </Centred>
    );
  }

  // DS-11a4 (F53): the company this person came from, while they still belong to it, is one press away — the switch
  // goes both ways, with no reload and nothing lost.
  const back = list.find((m) => m.id === previousCompanyId());
  return (
    <Centred title="Choose a company" titleRef={heading}>
      <p className="app-lede">
        Every action you take is recorded against the company you pick, and posting into the wrong
        one cannot be undone — so it is asked rather than assumed, even when there is only one.
      </p>
      <div className="app-companies">
        {list.map((m) => (
          <button key={m.id} type="button" className="app-company" onClick={() => setCompany(m.id)}>
            <span className="wb-company__mark">{m.name.charAt(0).toUpperCase()}</span>
            <span>
              <span className="app-company__name">{m.name}</span>
              <span className="app-company__meta">
                {/*
                  `roles`, never `role`. From ERP increment 1.2c the membership column is the
                  baseline — `member` for everyone who is not an administrator — so printing it
                  labelled every accountant, viewer and approver on the installation "member".
                  `roles` is the baseline plus the grants in force, which is what somebody
                  choosing between two companies is actually trying to tell apart.
                */}
                {(m.roles ?? [m.role]).join(' · ')} · {m.baseCurrency}
              </span>
            </span>
          </button>
        ))}
      </div>
      {back ? (
        <Cluster>
          {/* DS-11a4 review: the name says where it goes; the shell puts focus back on its company button. */}
          <Button aria-label={`Cancel, back to ${back.name}`} onClick={() => returnToCompany(back.id)}>
            Cancel
          </Button>
        </Cluster>
      ) : null}
    </Centred>
  );
}

export { ApiError };
